import { makeAutoObservable, observable, runInAction, untracked } from "mobx";
import { track, type AnalyticsSource } from "@/lib/analytics";
import type { RootStore } from "./root.store";
import type {
  TRequestWorkflow,
  TRequestWorkflowPendingTransition,
  TRequestWorkflowTransition,
  TRequestWorkflowTransitionActivity,
} from "@/types/request-workflow";
import type {
  TRequestUpdateWorkflowStateData,
  TRequestCreateWorkflowTransitionData,
  TRequestUpdateWorkflowTransitionData,
  TRequestSetWorkflowMoversData,
  TRequestSetWorkflowApproversData,
  TRequestWorkflowApprovalData,
} from "@/services/request-workflow.service";

/** A caller waiting on one form's workflow states from the next batch flush. */
interface WorkflowFetchWaiter {
  slug: string;
  formId: string;
  resolve: (workflows: TRequestWorkflow[]) => void;
  reject: (error: unknown) => void;
}

/**
 * How many form ids one batched workflow-states request may ask about. Mirrors
 * the endpoint's own cap (``WORKFLOW_STATE_BULK_FORM_LIMIT``) so a workspace with
 * more forms than this splits across requests instead of getting a 400.
 */
export const WORKFLOW_BATCH_LIMIT = 100;

export class RequestWorkflowStore {
  isLoading = false;
  isMutating = false;
  workflowsByForm: Map<string, TRequestWorkflow[]> = new Map();
  activitiesByForm: Map<string, TRequestWorkflowTransitionActivity[]> = new Map();
  // Forms we've successfully fetched at least once. ObservableMap (not Set) for
  // per-key reactivity — a state-select reading ``has(myFormId)`` only re-renders
  // when its own form resolves, not when any other form does. Using a Set would
  // cause a thundering-herd of re-renders across every row on the page.
  fetchedForms: Map<string, true> = new Map();
  // Forms whose last fetch FAILED. Same per-key ObservableMap reasoning as
  // ``fetchedForms``, and the two are mutually exclusive: a success clears the
  // failure and vice versa. Without this a rejected fetch is indistinguishable
  // from a slow one, so the status select sat on its loading row forever — which
  // is exactly how a 403 on the workflow-states read presented (NG-122).
  failedForms: Map<string, true> = new Map();
  // Pending transitions keyed by submission id. Pending approval is per submission
  // (the backend tracks it on RequestWorkflowSubmissionTransition), so the board
  // and list read pending for a specific request rather than form-wide.
  pendingBySubmission: Map<string, TRequestWorkflowPendingTransition[]> = new Map();
  fetchedPendingSubmissions: Map<string, true> = new Map();
  // In-flight ``fetchWorkflows`` calls keyed by formId. The submissions
  // list page renders ``IntakeSubmissionStatusSelect`` per row; without
  // dedup, N rows of the same form all see an empty cache and fire N
  // parallel workflow-states requests. The promise stays in the map
  // until the request resolves so concurrent callers receive the SAME
  // response without an extra round-trip.
  private inflightWorkflowFetches: Map<string, Promise<TRequestWorkflow[]>> = new Map();
  // In-flight per-submission pending fetches, keyed by submission id (same dedup
  // rationale as inflightWorkflowFetches — many cards, one request per submission).
  private inflightPendingFetches: Map<string, Promise<TRequestWorkflowPendingTransition[]>> =
    new Map();
  // Forms queued for the next coalesced batch request, with the callers waiting
  // on each. Every row of the submissions list asks for its own form in its mount
  // effect, and React flushes those effects in one synchronous pass — so queueing
  // them and flushing on a microtask turns a whole page's worth of per-form GETs
  // into ONE batched request (Sentry NEXT-GEN-FRONTEND-2B flagged the fan-out as
  // an N+1 API call on the /requests navigation).
  private queuedWorkflowFetches: WorkflowFetchWaiter[] = [];
  private isWorkflowBatchScheduled = false;

  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(
      this,
      {
        rootStore: false,
        workflowsByForm: observable,
        activitiesByForm: observable,
        fetchedForms: observable,
        failedForms: observable,
        pendingBySubmission: observable,
        fetchedPendingSubmissions: observable,
        inflightWorkflowFetches: false,
        inflightPendingFetches: false,
        queuedWorkflowFetches: false,
        isWorkflowBatchScheduled: false,
      } as never,
      { autoBind: true },
    );
  }

  getWorkflows(formId: string): TRequestWorkflow[] {
    return this.workflowsByForm.get(formId) ?? [];
  }

  hasFetchedForm(formId: string): boolean {
    return this.fetchedForms.has(formId);
  }

  /** Whether this form's last workflow-states fetch failed and none has succeeded since. */
  hasFailedForm(formId: string): boolean {
    return this.failedForms.has(formId);
  }

  /**
   * Drop the recorded failure for *formId* and fetch it again.
   *
   * The prefetch entry point skips forms already fetched or in flight but not
   * failed ones, so clearing the flag is only about the render: it takes the
   * select back to its loading row while the retry is out.
   */
  retryWorkflowsForForm(slug: string, formId: string): void {
    runInAction(() => {
      this.failedForms.delete(formId);
    });
    this.prefetchWorkflowsForForms(slug, [formId]);
  }

  // Eagerly warm the cache for every distinct form in a list, without blocking
  // render. Skips forms already fetched or in flight so a re-render (filter,
  // sort, scroll) doesn't re-issue requests. Called from an effect (outside any
  // reaction), so the dedup reads are wrapped in ``untracked`` to satisfy
  // ``observableRequiresReaction``.
  //
  // Queues rather than requesting immediately: every call in the same synchronous
  // pass (one per list row) is flushed as a single batched request.
  prefetchWorkflowsForForms(slug: string, formIds: Iterable<string>): void {
    untracked(() => {
      for (const formId of new Set(formIds)) {
        if (this.fetchedForms.has(formId) || this.inflightWorkflowFetches.has(formId)) continue;
        // Nothing awaits a prefetch, so an unhandled rejection here escapes as a
        // process-level error (#4200, Sentry NEXT-GEN-FRONTEND-1K). Degradation,
        // not breakage: the row renders without its workflow and re-fetches on
        // demand. The logging moved into the batch runner below — it is the one
        // place that knows which ids shared the failed request — so the handler
        // here only marks the promise handled.
        this.queueWorkflowFetch(slug, formId).catch(() => undefined);
      }
    });
  }

  // Register a form for the next batch flush and hand back the promise for it.
  // Shares ``inflightWorkflowFetches`` with the single-form ``fetchWorkflows`` so
  // either entry point dedups against a queued batch.
  private queueWorkflowFetch(slug: string, formId: string): Promise<TRequestWorkflow[]> {
    const existing = this.inflightWorkflowFetches.get(formId);
    if (existing) return existing;

    const promise = new Promise<TRequestWorkflow[]>((resolve, reject) => {
      this.queuedWorkflowFetches.push({ slug, formId, resolve, reject });
    });
    this.inflightWorkflowFetches.set(formId, promise);

    if (!this.isWorkflowBatchScheduled) {
      this.isWorkflowBatchScheduled = true;
      queueMicrotask(() => {
        this.isWorkflowBatchScheduled = false;
        const waiters = this.queuedWorkflowFetches;
        this.queuedWorkflowFetches = [];
        void this.flushWorkflowBatch(waiters);
      });
    }
    return promise;
  }

  private async flushWorkflowBatch(waiters: WorkflowFetchWaiter[]): Promise<void> {
    // A workspace slug is part of the URL, so waiters can only share a request
    // when they share a slug (they always do in practice — one page, one
    // workspace — but a slug change mid-flush must not mix them up).
    const bySlug = new Map<string, WorkflowFetchWaiter[]>();
    for (const waiter of waiters) {
      const group = bySlug.get(waiter.slug);
      if (group) group.push(waiter);
      else bySlug.set(waiter.slug, [waiter]);
    }

    const requests: Promise<void>[] = [];
    for (const [slug, group] of bySlug) {
      // Stay under the endpoint's per-request id cap (a workspace can hold more
      // forms than one batch may ask about).
      for (let start = 0; start < group.length; start += WORKFLOW_BATCH_LIMIT) {
        requests.push(
          this.runWorkflowBatch(slug, group.slice(start, start + WORKFLOW_BATCH_LIMIT)),
        );
      }
    }
    await Promise.all(requests);
  }

  private async runWorkflowBatch(slug: string, waiters: WorkflowFetchWaiter[]): Promise<void> {
    const formIds = [...new Set(waiters.map((waiter) => waiter.formId))];
    try {
      const byForm = await this.rootStore.requestWorkflowService.listWorkflowStatesForForms(
        slug,
        formIds,
      );
      // Keep a plain snapshot to resolve waiters from: reading the observable
      // cache back out here would be an observable read outside a reaction.
      const resolved = new Map<string, TRequestWorkflow[]>(
        // A form the response omits no longer exists (it was soft-deleted while
        // its requests kept listing). Cache an empty rule set for it so the
        // status select degrades to unconstrained instead of asking again on
        // every mount.
        formIds.map((formId) => [formId, byForm[formId] ?? []]),
      );
      runInAction(() => {
        for (const [formId, workflows] of resolved) {
          this.workflowsByForm.set(formId, workflows);
          this.fetchedForms.set(formId, true);
          // A success supersedes any earlier failure — the two flags are the
          // select's loading/error/ready trichotomy and must never both be set.
          this.failedForms.delete(formId);
        }
      });
      for (const waiter of waiters) {
        this.inflightWorkflowFetches.delete(waiter.formId);
      }
      for (const waiter of waiters) {
        waiter.resolve(resolved.get(waiter.formId) ?? []);
      }
    } catch (error) {
      for (const waiter of waiters) {
        this.inflightWorkflowFetches.delete(waiter.formId);
      }
      // Record the failure so the status select can say so and offer a retry.
      // Before this it only knew "not fetched yet", so a rejected request looked
      // exactly like a slow one and the dropdown spun forever (NG-122).
      runInAction(() => {
        for (const formId of formIds) {
          this.failedForms.set(formId, true);
        }
      });
      // A failed background warm degrades (the select falls back to
      // unconstrained) rather than breaking the page, so warn — but never
      // silently: the ids are what makes it actionable. Same (message, context,
      // error) shape as the sibling prefetch log above.
      console.warn("[request-workflow] workflow prefetch failed", { slug, formIds }, error);
      for (const waiter of waiters) {
        waiter.reject(error);
      }
    }
  }

  getPendingForSubmission(submissionId: string): TRequestWorkflowPendingTransition[] {
    return this.pendingBySubmission.get(submissionId) ?? [];
  }

  // Warm per-submission pending for the requests on screen. Takes (formId,
  // submissionId) pairs; skips submissions already fetched or in flight. Called
  // from an effect, so dedup reads are wrapped in ``untracked``.
  prefetchPendingForSubmissions(
    slug: string,
    pairs: Iterable<{ formId: string; submissionId: string }>,
  ): void {
    untracked(() => {
      const seen = new Set<string>();
      for (const { formId, submissionId } of pairs) {
        if (seen.has(submissionId)) continue;
        seen.add(submissionId);
        if (
          this.fetchedPendingSubmissions.has(submissionId) ||
          this.inflightPendingFetches.has(submissionId)
        ) {
          continue;
        }
        void this.fetchPendingForSubmission(slug, formId, submissionId).catch((error: unknown) => {
          // Same as above: an unawaited prefetch must not reject into nothing.
          console.warn(
            "[request-workflow] pending-transitions prefetch failed",
            { slug, formId, submissionId },
            error,
          );
        });
      }
    });
  }

  // Seed the pending cache from submission rows that already carry their
  // pending transitions (the submissions list/board/detail payloads fold them
  // in — #2069). Marks each submission fetched so the per-row
  // ``prefetchPendingForSubmissions`` becomes a no-op instead of firing one
  // request per row. Idempotent: re-seeding the same rows just rewrites the
  // same values, so calling it on every page (including load-more) is safe.
  // Rows without the field (e.g. an optimistic local row) are skipped so a real
  // fetch can still fill them in.
  seedPendingForSubmissions(
    submissions: Iterable<{
      id: string;
      pending_transitions?: TRequestWorkflowPendingTransition[];
    }>,
  ): void {
    runInAction(() => {
      for (const submission of submissions) {
        if (!Array.isArray(submission.pending_transitions)) continue;
        this.pendingBySubmission.set(submission.id, submission.pending_transitions);
        this.fetchedPendingSubmissions.set(submission.id, true);
      }
    });
  }

  async fetchPendingForSubmission(
    slug: string,
    formId: string,
    submissionId: string,
  ): Promise<TRequestWorkflowPendingTransition[]> {
    const existing = this.inflightPendingFetches.get(submissionId);
    if (existing) return existing;

    const promise = (async () => {
      try {
        const pending = await this.rootStore.requestWorkflowService.listPendingTransitions(
          slug,
          formId,
          submissionId,
        );
        runInAction(() => {
          this.pendingBySubmission.set(submissionId, pending);
          this.fetchedPendingSubmissions.set(submissionId, true);
        });
        return pending;
      } finally {
        this.inflightPendingFetches.delete(submissionId);
      }
    })();
    this.inflightPendingFetches.set(submissionId, promise);
    return promise;
  }

  async fetchWorkflows(slug: string, formId: string): Promise<TRequestWorkflow[]> {
    const existing = this.inflightWorkflowFetches.get(formId);
    if (existing) return existing;

    runInAction(() => {
      this.isLoading = true;
    });
    const promise = (async () => {
      try {
        const workflows = await this.rootStore.requestWorkflowService.listWorkflowStates(
          slug,
          formId,
        );
        runInAction(() => {
          this.workflowsByForm.set(formId, workflows);
          this.fetchedForms.set(formId, true);
          this.failedForms.delete(formId);
        });
        return workflows;
      } finally {
        runInAction(() => {
          this.isLoading = false;
        });
        this.inflightWorkflowFetches.delete(formId);
      }
    })();
    this.inflightWorkflowFetches.set(formId, promise);
    return promise;
  }

  async updateWorkflowState(
    slug: string,
    formId: string,
    stateId: string,
    data: TRequestUpdateWorkflowStateData,
    source: AnalyticsSource = "page",
  ): Promise<TRequestWorkflow> {
    this.isMutating = true;
    try {
      const updated = await this.rootStore.requestWorkflowService.updateWorkflowState(
        slug,
        formId,
        stateId,
        data,
      );
      runInAction(() => {
        this.workflowsByForm.set(
          formId,
          this.getWorkflows(formId).map((w) => (w.id === updated.id ? updated : w)),
        );
      });
      track("request_workflow_state_updated", {
        form_id: formId,
        state_id: stateId,
        fields: Object.keys(data),
        source,
      });
      return updated;
    } finally {
      runInAction(() => {
        this.isMutating = false;
      });
    }
  }

  async resetWorkflow(
    slug: string,
    formId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    this.isMutating = true;
    try {
      await this.rootStore.requestWorkflowService.resetWorkflow(slug, formId);
      // Each configuration write reports before its refetch, like the approval
      // events below: the write landed even if reloading the workflow fails.
      track("request_workflow_reset", { form_id: formId, source });
      await this.fetchWorkflows(slug, formId);
    } finally {
      runInAction(() => {
        this.isMutating = false;
      });
    }
  }

  async createTransition(
    slug: string,
    formId: string,
    data: TRequestCreateWorkflowTransitionData,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    this.isMutating = true;
    try {
      await this.rootStore.requestWorkflowService.createTransition(slug, formId, data);
      track("request_workflow_transition_created", {
        form_id: formId,
        workflow_id: data.workflow_id,
        to_status_id: data.transition_status_id,
        source,
      });
      await this.fetchWorkflows(slug, formId);
    } finally {
      runInAction(() => {
        this.isMutating = false;
      });
    }
  }

  async updateTransition(
    slug: string,
    formId: string,
    transitionId: string,
    data: TRequestUpdateWorkflowTransitionData,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    this.isMutating = true;
    try {
      await this.rootStore.requestWorkflowService.updateTransition(
        slug,
        formId,
        transitionId,
        data,
      );
      // Each control sends only its own setting, so the body's keys are the change.
      track("request_workflow_transition_updated", {
        form_id: formId,
        transition_id: transitionId,
        fields: Object.keys(data),
        source,
      });
      await this.fetchWorkflows(slug, formId);
    } finally {
      runInAction(() => {
        this.isMutating = false;
      });
    }
  }

  async deleteTransition(
    slug: string,
    formId: string,
    transitionId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    this.isMutating = true;
    try {
      await this.rootStore.requestWorkflowService.deleteTransition(slug, formId, transitionId);
      track("request_workflow_transition_deleted", {
        form_id: formId,
        transition_id: transitionId,
        source,
      });
      await this.fetchWorkflows(slug, formId);
    } finally {
      runInAction(() => {
        this.isMutating = false;
      });
    }
  }

  async setMovers(
    slug: string,
    formId: string,
    transitionId: string,
    data: TRequestSetWorkflowMoversData,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    this.isMutating = true;
    try {
      await this.rootStore.requestWorkflowService.setMovers(slug, formId, transitionId, data);
      track("request_workflow_transition_updated", {
        form_id: formId,
        transition_id: transitionId,
        fields: ["movers"],
        source,
      });
      await this.fetchWorkflows(slug, formId);
    } finally {
      runInAction(() => {
        this.isMutating = false;
      });
    }
  }

  async setApprovers(
    slug: string,
    formId: string,
    transitionId: string,
    data: TRequestSetWorkflowApproversData,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    this.isMutating = true;
    try {
      await this.rootStore.requestWorkflowService.setApprovers(slug, formId, transitionId, data);
      track("request_workflow_transition_updated", {
        form_id: formId,
        transition_id: transitionId,
        fields: ["approvers"],
        source,
      });
      await this.fetchWorkflows(slug, formId);
    } finally {
      runInAction(() => {
        this.isMutating = false;
      });
    }
  }

  async submitApproval(
    slug: string,
    formId: string,
    transitionId: string,
    data: TRequestWorkflowApprovalData,
    source = "page",
  ): Promise<void> {
    this.isMutating = true;
    try {
      const result = await this.rootStore.requestWorkflowService.submitApproval(
        slug,
        formId,
        transitionId,
        data,
      );
      this.trackApprovalEvent("request_approval_submitted", transitionId, data, source);
      if (data.submission_id) {
        await this.fetchPendingForSubmission(slug, formId, data.submission_id);
        // When this vote tipped the transition over its approval threshold, the
        // backend executed the state move in the same request. Reflect it on the
        // board now so the card lands in its destination column without a refresh.
        if (result.transition_executed && result.new_status_id) {
          await this.rootStore.intakeStore.relocateSubmissionToState(
            slug,
            data.submission_id,
            result.new_status_id,
          );
        }
      }
    } finally {
      runInAction(() => {
        this.isMutating = false;
      });
    }
  }

  async submitRejection(
    slug: string,
    formId: string,
    transitionId: string,
    data: TRequestWorkflowApprovalData,
    source = "page",
  ): Promise<void> {
    this.isMutating = true;
    try {
      const result = await this.rootStore.requestWorkflowService.submitRejection(
        slug,
        formId,
        transitionId,
        data,
      );
      this.trackApprovalEvent("request_rejection_submitted", transitionId, data, source);
      if (data.submission_id) {
        await this.fetchPendingForSubmission(slug, formId, data.submission_id);
        // A rejection moves the submission to the transition's configured
        // rejection_state when one is set. Relocate the card the same way an
        // approval does so the board reflects it without a refresh.
        if (result.rejection_status_id) {
          await this.rootStore.intakeStore.relocateSubmissionToState(
            slug,
            data.submission_id,
            result.rejection_status_id,
          );
        }
      }
    } finally {
      runInAction(() => {
        this.isMutating = false;
      });
    }
  }

  async requestApproval(
    slug: string,
    formId: string,
    transitionId: string,
    submissionId: string,
    source = "page",
  ): Promise<void> {
    this.isMutating = true;
    try {
      await this.rootStore.requestWorkflowService.requestApproval(
        slug,
        formId,
        transitionId,
        submissionId,
      );
      track("request_approval_requested", {
        request_id: submissionId,
        transition_id: transitionId,
        source,
      });
      await this.fetchPendingForSubmission(slug, formId, submissionId);
    } finally {
      runInAction(() => {
        this.isMutating = false;
      });
    }
  }

  async cancelApprovalRequest(
    slug: string,
    formId: string,
    transitionId: string,
    submissionId: string,
    source = "page",
  ): Promise<void> {
    this.isMutating = true;
    try {
      await this.rootStore.requestWorkflowService.cancelApprovalRequest(
        slug,
        formId,
        transitionId,
        submissionId,
      );
      track("request_approval_cancelled", {
        request_id: submissionId,
        transition_id: transitionId,
        source,
      });
      await this.fetchPendingForSubmission(slug, formId, submissionId);
    } finally {
      runInAction(() => {
        this.isMutating = false;
      });
    }
  }

  /** An approver's vote, emitted as soon as the write lands: the pending and
   *  board refreshes after it are reads, and their failing must not lose it. */
  private trackApprovalEvent(
    event: "request_approval_submitted" | "request_rejection_submitted",
    transitionId: string,
    data: TRequestWorkflowApprovalData,
    source: string,
  ): void {
    track(event, {
      request_id: data.submission_id ?? null,
      transition_id: transitionId,
      source,
    });
  }

  // The pending transition for ONE submission, paired with the matching transition
  // definition (for approvers/required_approvals etc.). Keyed by submission, so a
  // request on another submission never surfaces here.
  getPendingTransitionDetails(
    submissionId: string,
    formId: string,
  ):
    | {
        workflow: TRequestWorkflow;
        transition: TRequestWorkflowTransition;
        pending: TRequestWorkflowPendingTransition;
      }
    | undefined {
    const pendingList = this.getPendingForSubmission(submissionId);
    if (pendingList.length === 0) return undefined;
    const pending = pendingList[0];
    const workflows = this.getWorkflows(formId);
    for (const wf of workflows) {
      const transition = wf.transitions.find((t) => t.id === pending.workflow_transition_id);
      if (transition) {
        return { workflow: wf, transition, pending };
      }
    }
    return undefined;
  }

  getActivities(formId: string): TRequestWorkflowTransitionActivity[] {
    return this.activitiesByForm.get(formId) ?? [];
  }

  async fetchActivities(
    slug: string,
    formId: string,
    params?: Record<string, string>,
  ): Promise<TRequestWorkflowTransitionActivity[]> {
    this.isLoading = true;
    try {
      const activities = await this.rootStore.requestWorkflowService.listActivity(
        slug,
        formId,
        params,
      );
      runInAction(() => {
        this.activitiesByForm.set(formId, activities);
      });
      return activities;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }
}
